// 2026-05-26 - FormuLearning - Step 3 API 호출
// 2026-09-26 수정 - 이슈 #95 단계 3-5: `uploadResults`·`downloadTemplateUrl` 삭제(`M84-75` 업로드·양식 폐기).
// 파일 위치: frontend/src/api/step3.ts
// 2026-09-24 수정 - 이슈 #75(G4): `RLDLot` 의 `grid_locked`(격자 잠금 폐지)·제외 3필드(Lot 제외 폐지) 삭제.

import client from './client'
import type { ApiResponse } from '../types/api'

export type XMode = 'selected' | 'selected(non-weight)' | 'fixed' | 'balance'

export interface XVariable {
  excipient: string
  function: string
  mode: XMode
  min_pct: number
  max_pct: number
  sort_order: number
  // G4(이슈 #33) — RLD 주성분에서 전파된 잠금 행. true면 이름·기능·모드는 잠기고 %(숫자)만 편집 가능.
  // JSONB 규약이므로 마이그레이션 불필요(선택적 필드).
  is_active?: boolean
}

export type YTarget = 'maximize' | 'minimize' | 'target'

export interface YVariable {
  response: string
  target: YTarget
  constraints: string
  is_selected: boolean
  target_value: number | null
  // 2026-09-22 - 이슈 #67 S4-2: 용출액별 f2 파생 행(BR-RND-05, 인터페이스 v1.13). **서버가 만든다** —
  //   화면은 표시만 하고, 저장 시 보낸 파생 행은 서버가 버리고 활성 Lot 기준으로 다시 만든다.
  is_derived?: boolean
  derived_from?: 'f2'
  solution?: string   // 표시 라벨(`pH 1.2`) — 파생 행 이름은 `f2 (pH 1.2)`
}

export interface DissolutionTimePoint {
  time: number
  dissolution: number
  sd: number
}

export interface PhProfile {
  ph: string
  time_points: DissolutionTimePoint[]
  /** 반복 횟수(`M84-15`, 데이터 v1.12 §3.8). 미입력은 null — 0회가 아니다. */
  n?: number | null
}

// 2026-07-18 - 다중 lot 확장 (이슈 #40). 대조약(RLD) 용출 시험을 여러 lot으로 진행할 수 있다.
// 하나의 lot이 pH별(용액별) 용출 프로파일을 가진다.
// 2026-07-23 - 이슈 #50(P1): 위치 기반 배열을 폐기하고 Lot을 DB id로 대면한다(BR-LOT-08).
export interface RLDLot {
  /** Lot DB id — 안정 식별자. 서버 조립 응답에 항상 포함되며, DB에 Lot이 없는 초기 상태만 null. */
  id: string | null
  /** Lot 번호(DB lot_no). 탭 라벨 "Lot N · MM/DD"의 N(BR-LOT-12). 숨김으로 결번이 생길 수 있다. */
  no: number | null
  /** 사용자가 입력한 lot 표기(제조번호 등) = Lot.label. 탭 번호와 별개다. */
  lot_no: string
  test_date: string
  ph_profiles: PhProfile[]
}

// rld_experiment JSONB 규약(하위호환): 구본은 { lot_no, test_date, ph_profiles }(단일 lot) 형태였고,
// 신본은 { lots: RLDLot[], active_lot: number }(다중 lot) 형태다. 로드 시 normalizeRldExperiment로 정규화한다.
export interface RLDExperiment {
  lots: RLDLot[]
  active_lot: number // 진행 중 lot 인덱스(0-base)
  // 구본 호환용 optional 필드 — 읽기 정규화 전 원본에 존재할 수 있다.
  lot_no?: string
  test_date?: string
  ph_profiles?: PhProfile[]
}

// 구 단일-lot shape 또는 신 다중-lot shape 어느 쪽이든 { lots, active_lot }로 정규화한다.
// 이슈 #50(P1): id·no를 보존한다(grid_locked·제외 필드는 #75 삭제). 구 데이터(무id)는 null로 정규화되고,
// 첫 저장 시 Lot API가 id를 부여한다.
export function normalizeRldExperiment(raw: unknown): RLDExperiment {
  const r = (raw ?? {}) as Partial<RLDExperiment>
  if (Array.isArray(r.lots)) {
    const lots = r.lots.map((l) => ({
      id: l?.id ?? null,
      no: l?.no ?? null,
      lot_no: l?.lot_no ?? '',
      test_date: l?.test_date ?? '',
      ph_profiles: l?.ph_profiles ?? [],
    }))
    const active = Number.isInteger(r.active_lot) ? (r.active_lot as number) : 0
    return { lots, active_lot: Math.min(Math.max(0, active), Math.max(0, lots.length - 1)) }
  }
  // 구본(단일 lot) → lots[0]로 승격. 완전히 빈 값이면 빈 lot 1개를 둔다.
  return {
    lots: [{
      id: null,
      no: null,
      lot_no: r.lot_no ?? '',
      test_date: r.test_date ?? '',
      ph_profiles: r.ph_profiles ?? [],
    }],
    active_lot: 0,
  }
}

export interface DissolutionProfile {
  ph: string
  data: DissolutionTimePoint[]
  /** 반복 횟수(`M84-15`) — 그래프 범례 `pH 1.2 (n=3)` 의 소스. 미입력은 null. */
  n?: number | null
}

export interface Step3Run {
  id: string
  run_number: number
  x_values: Record<string, number>
  y_values: Record<string, number>
  dissolution_profiles: DissolutionProfile[]
  // 2026-07-20 - 이슈 #42: f2는 시스템 계산값(R1). y_values가 아니라 이 필드가 정본이다.
  f2: number | null
  f2_by_solution: Record<string, number>
  f2_reason: string | null
  status: 'awaiting' | 'completed'
}

export interface Step3Data {
  id: string
  project_id: string
  total_weight_mg: number | null
  total_weight_manual: boolean
  x_variables: XVariable[]
  y_variables: YVariable[]
  rld_experiment: RLDExperiment
  runs_algorithm: 'lhs' | 'random' | 'user_defined' | null
  runs_count: number | null
  runs: Step3Run[]
  ai_summary: string | null
  ai_summary_ko: string | null
  created_at: string
  updated_at: string
}

export async function getStep3(projectId: string): Promise<Step3Data> {
  const { data } = await client.get<ApiResponse<Step3Data>>(`/step3/${projectId}/`)
  return data.data!
}

export async function updateStep3(projectId: string, body: Partial<Step3Data>): Promise<Step3Data> {
  const { data } = await client.put<ApiResponse<Step3Data>>(`/step3/${projectId}/`, body)
  return data.data!
}

// 2026-07-18 - 이슈 #40: Step3 "Save and continue" 시 AI Summary 생성·영구 저장(Vertex).
export async function generateStep3Summary(projectId: string): Promise<{ ai_summary: string | null; ai_summary_ko: string | null }> {
  const { data } = await client.post<ApiResponse<{ ai_summary: string | null; ai_summary_ko: string | null }>>(`/step3/${projectId}/ai-summary`)
  return data.data!
}

/**
 * Initial Design 생성. 2026-08-30(#58 S3): ~~`mode`(replace/append) 3지선다~~ 폐기 —
 * 기존 배치 처리는 **서버가 결과 유무로 판정**한다(BR-RND-10).
 *
 * `preserved_count > 0`이면 **결과가 있어 보존했고 생성하지 않았다**는 뜻이다.
 * 오류가 아니라 정상 응답이며, 호출부는 이를 재료로 **덮어쓸지 묻는다**.
 *
 * 2026-09-09(#72, M901-20): `overwrite` 신설. 사용자가 «값을 지우고 새 초기값으로
 * 덮어쓰겠다»고 확인하면 `true`로 다시 부른다. 예/아니오 하나이며 3지선다가 아니다.
 */
export async function generateRuns(
  projectId: string,
  algorithm: string,
  count: number,
  xVariables: XVariable[],
  overwrite = false,
): Promise<{ runs: Step3Run[]; preserved_count: number; deleted_count: number }> {
  const { data } = await client.post<ApiResponse<{ runs: Step3Run[]; preserved_count: number; deleted_count: number }>>(
    `/step3/${projectId}/generate-runs`,
    { algorithm, count, x_variables: xVariables, overwrite },
  )
  return data.data!
}

// 2026-07-20 - 이슈 #42: 시험약 용출 그리드 저장. 저장 즉시 백엔드가 f2를 재계산해 돌려준다.
export async function saveStep3Dissolution(
  projectId: string,
  batchId: string,
  profiles: DissolutionProfile[],
): Promise<Step3Run> {
  const { data } = await client.put<ApiResponse<Step3Run>>(
    `/step3/${projectId}/batches/${batchId}/dissolution`,
    { dissolution_profiles: profiles },
  )
  return data.data!
}

